/**
 * @file DocumentOutputModal.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-10-02
 * @description The Export window (icon: ios_share) for invoices, credit notes,
 *              purchase orders and bills — one, or a selection — from a row's
 *              menu, its right-click menu, a bulk action, or the document's own
 *              modal header. The server decides the rules
 *              (services/documentOutputService.js); this collects the choices.
 *
 *              PREVIEW, NOT A VIEWER. The document is drawn as page images
 *              (PdfCanvasPreview) — no toolbar to save or print it from — so
 *              what is done with it goes through Print / Download / Email here,
 *              each of which is logged, as is the preview itself.
 *
 *              EMAIL IS TO YOURSELF. Export sends only to the signed-in user
 *              (the server ignores any other address). Sending a document to a
 *              customer or supplier is its own workflow.
 *
 *              The choices:
 *
 *              - Save or email several as ONE PDF, or keep them separate (a zip
 *                to save, one attachment each to email).
 *              - Save on the on-screen template by default, or the print
 *                template. Email is always on-screen. Print is always print.
 *
 *              What to do with it is picked in the footer (ExportModeFooter),
 *              as in every other export.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import TickCheckbox from './TickCheckbox';
import PdfCanvasPreview from './PdfCanvasPreview';
import ExportModeFooter, { revealOnMount } from './ExportModeFooter';
import './Formulations.css';
import './TableExportModal.css';
import './ExportLayoutOptions.css';
import './DocumentOutputModal.css';

const TYPE_TEXT = {
  invoice: { noun: 'invoice', plural: 'invoices', sender: 'accounts', hasPrintTemplate: true, hasStamps: true },
  purchase_order: { noun: 'purchase order', plural: 'purchase orders', sender: 'admin', hasPrintTemplate: false, hasStamps: false },
  bill: { noun: 'bill', plural: 'bills', sender: 'accounts', hasPrintTemplate: true, hasStamps: false },
};

const MODES = [
  { key: 'download', icon: 'download', label: 'Download', hint: 'Save the PDF to this device' },
  { key: 'print', icon: 'print', label: 'Print', hint: 'Send to your printer' },
  { key: 'email', icon: 'mail', label: 'Email', hint: 'Attach to an email' },
];


function Chip({ active, icon, label, onClick, title }) {
  return (
    <button type="button" className={`export-layout-fit${active ? ' active' : ''}`}
            aria-pressed={active} title={title} onClick={onClick}>
      {icon && <span className="material-symbols-rounded">{icon}</span>}
      <span className="export-layout-fit-label">{label}</span>
    </button>
  );
}

/* The options column. The footer's slide-out buttons are measured by
   ExportModeFooter, which mounts with them — see CLAUDE.md on modal slide-outs. */
function OutputBody({ state, set, text, count, myEmail }) {
  const many = count > 1;

  return (
    <div className="table-export-body document-output-body">
      {state.error && <div className="formulation-error">{state.error}</div>}

      <p className="fm-export-summary">
        {count} {count === 1 ? text.noun : text.plural} selected.
      </p>

      {/* Only when there is something to choose: a single PO has no template,
          stamps or file options, and an empty box reads as a broken one. */}
      {((many && state.mode !== 'print') || text.hasPrintTemplate || text.hasStamps) && (
      <div className="export-layout">
        <div className="export-layout-head">Document</div>

        {many && state.mode !== 'print' && (
          <div className="export-layout-line">
            <span className="export-layout-key">Files</span>
            <div className="export-layout-fits">
              <Chip active={state.combine} icon="picture_as_pdf" label="One combined PDF"
                    onClick={() => set({ combine: true })} />
              <Chip active={!state.combine} icon="file_copy"
                    label={state.mode === 'email' ? 'Separate attachments' : 'Separate PDFs (zip)'}
                    onClick={() => set({ combine: false })} />
            </div>
          </div>
        )}

        {text.hasPrintTemplate && (
          <div className="export-layout-line">
            <span className="export-layout-key">Template</span>
            <div className="export-layout-fits">
              {state.mode === 'download' ? (
                <>
                  <Chip active={state.template === 'screen'} icon="description" label="On-screen"
                        title="The letterhead copy, as it is emailed"
                        onClick={() => set({ template: 'screen' })} />
                  <Chip active={state.template === 'print'} icon="print" label="Print"
                        title="The layout for our pre-printed paper"
                        onClick={() => set({ template: 'print' })} />
                </>
              ) : (
                <span className="document-output-fixed">
                  {state.mode === 'print' ? 'Print template — for the paper in the printer' : 'On-screen template'}
                </span>
              )}
            </div>
          </div>
        )}

        {text.hasStamps && (
          <div className="export-layout-line">
            <span className="export-layout-key">Stamps</span>
            <div className="document-output-ticks">
              <TickCheckbox id="doc-stamp-paid" checked={state.showPaidStamp} label="Paid"
                            onChange={(e) => set({ showPaidStamp: e.target.checked })} />
              <TickCheckbox id="doc-stamp-overdue" checked={state.showOverdueStamp} label="Overdue"
                            onChange={(e) => set({ showOverdueStamp: e.target.checked })} />
            </div>
          </div>
        )}
        {text.hasStamps && (
          <p className="export-layout-note">A stamp only appears on an invoice it is true of.</p>
        )}
      </div>
      )}

      {/* The mode is picked in the footer; what it needs stays here. */}
      {state.mode === 'email' && (
        <div className="fm-export-email" ref={revealOnMount}>
          <p className="table-export-note document-output-to">
            <span className="material-symbols-rounded">mail</span>
            Sent to you{myEmail ? ` — ${myEmail}` : ''}. Exports go to your own inbox only; to send a document to a
            customer or supplier, use its Send action.
          </p>

          <div className="fm-export-field">
            <label htmlFor="doc-subject">Subject</label>
            <Input id="doc-subject" type="text" value={state.subject}
                   placeholder="Default subject will be used if blank"
                   onChange={(e) => set({ subject: e.target.value })} />
          </div>
          <div className="fm-export-field">
            <label htmlFor="doc-message">Message</label>
            <Textarea id="doc-message" rows={3} value={state.message}
                      placeholder="Optional covering message…"
                      onChange={(e) => set({ message: e.target.value })} />
          </div>
        </div>
      )}

      {state.mode === 'print' && (
        <p className="table-export-note">
          Goes to your printer. {many ? 'Each is sent as its own job. ' : ''}The printer is checked first, so an
          offline print bridge says so rather than swallowing the job.
        </p>
      )}
    </div>
  );
}

export default function DocumentOutputModal({ type, ids, initialMode = 'download', user, onClose, onDone }) {
  // Whose inbox an emailed export goes to — shown, not chosen.
  const [myEmail, setMyEmail] = useState(user?.email || null);
  useEffect(() => {
    if (myEmail) return undefined;
    let alive = true;
    fetch(`${API_URL}/api/users/me`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (alive) setMyEmail(d?.user?.email || d?.email || null); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Which of the selected documents the preview shows.
  const [previewIndex, setPreviewIndex] = useState(0);
  const text = TYPE_TEXT[type];
  const count = ids.length;

  const [state, setState] = useState(() => ({
    mode: initialMode,
    combine: true,
    template: 'screen',
    showPaidStamp: false,
    showOverdueStamp: false,
    subject: '',
    message: '',
    busy: false,
    error: '',
  }));
  const set = (patch) => setState((prev) => ({ ...prev, ...patch }));

  const submit = async () => {
    set({ busy: true, error: '' });
    try {
      const res = await fetch(`${API_URL}/api/documents/${type}/output`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ids,
          mode: state.mode,
          combine: state.combine,
          template: state.template,
          showPaidStamp: state.showPaidStamp,
          showOverdueStamp: state.showOverdueStamp,
          ...(state.mode === 'email'
            ? { subject: state.subject, message: state.message }
            : {}),
        }),
      });

      if (state.mode === 'download') {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Could not build the PDF');
        }
        const blob = await res.blob();
        const disposition = res.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = match ? match[1] : `${text.plural.replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        onDone?.(count === 1 ? 'Downloaded' : `Downloaded ${count} ${text.plural}`);
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not ${state.mode} the ${text.plural}`);
      onDone?.(data.message);
    } catch (e) {
      set({ error: e.message, busy: false });
      return;
    }
    set({ busy: false });
  };

  const verb = state.mode === 'download' ? 'Download' : state.mode === 'print' ? 'Print' : 'Send';
  const icon = state.mode === 'download' ? 'download' : state.mode === 'print' ? 'print' : 'send';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Export ${count === 1 ? text.noun : `${count} ${text.plural}`}`}
      size="xl"
      className="formulation-export table-export document-output"
      footer={
        <ExportModeFooter modes={MODES} mode={state.mode} onMode={(key) => set({ mode: key })}
                          busy={state.busy}
                          stepLabel={`What to do with ${count === 1 ? 'it' : 'them'}`}>
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={state.busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={submit} disabled={state.busy || count === 0}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${state.busy ? ' spinning' : ''}`}>
                  {state.busy ? 'progress_activity' : icon}
                </span>
              </div>
              <span className="btn-label">{state.busy ? 'Working…' : verb}</span>
            </button>
          </div>
        </ExportModeFooter>
      }
    >
      <div className="document-output-layout">
        <div className="document-output-preview">
          {count > 1 && (
            <div className="document-output-pager">
              <button type="button" className="btn-icon" disabled={previewIndex === 0}
                      onClick={() => setPreviewIndex(i => Math.max(0, i - 1))} title="Previous">
                <span className="material-symbols-rounded">chevron_left</span>
              </button>
              <span>{previewIndex + 1} of {count}</span>
              <button type="button" className="btn-icon" disabled={previewIndex >= count - 1}
                      onClick={() => setPreviewIndex(i => Math.min(count - 1, i + 1))} title="Next">
                <span className="material-symbols-rounded">chevron_right</span>
              </button>
            </div>
          )}
          <PdfCanvasPreview
            loadKey={`${type}:${ids[previewIndex]}:${state.template}:${state.showPaidStamp}:${state.showOverdueStamp}`}
            load={async () => {
              const r = await fetch(`${API_URL}/api/documents/${type}/${ids[previewIndex]}/preview`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ template: state.template, showPaidStamp: state.showPaidStamp, showOverdueStamp: state.showOverdueStamp })
              });
              if (!r.ok) {
                const body = await r.json().catch(() => ({}));
                throw new Error(body.error || 'Could not build the preview');
              }
              return r.arrayBuffer();
            }}
          />
        </div>
        <OutputBody state={state} set={set} text={text} count={count} myEmail={myEmail} />
      </div>
    </Modal>
  );
}
